/**
 * Face Match — Rudrik
 * ====================
 * Mock mode: user uploads a selfie + ID photo. Frontend base64-encodes both
 * and POSTs to /api/face-match. Backend currently returns mock AWS Rekognition
 * output interpreted by Claude Haiku 4.5. When real AWS keys land, zero UI
 * change is required.
 */
import React, { useState, useRef } from "react";
import axios from "axios";
import {
  ScanFace, Upload, Loader2, ShieldCheck, AlertTriangle, X,
  CheckCircle2, XCircle, Eye, Sparkles, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromFaceMatch } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/face-match`;

const VERDICT_COLOR = {
  PASS: "#22c55e", FAIL: "#dc2626", MANUAL_REVIEW: "#f59e0b",
};
const RISK_COLOR = {
  LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#ef4444", CRITICAL: "#dc2626",
};

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function UploadBox({ label, file, onPick, onClear, testId }) {
  const inputRef = useRef(null);
  return (
    <div style={{
      background: "#0d1117", border: `1px dashed ${file ? "#22c55e55" : "#1e2530"}`,
      borderRadius: 12, padding: 18, display: "flex", flexDirection: "column",
      alignItems: "center", gap: 12, minHeight: 220,
    }}>
      <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
      {file ? (
        <>
          <img src={file.preview} alt={label} style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 8, border: "1px solid #1e2530" }} />
          <div style={{ color: "#cbd5e1", fontSize: 11, textAlign: "center" }}>
            {file.name}<br />
            <span style={{ color: "#64748b" }}>{(file.size / 1024).toFixed(1)} KB</span>
          </div>
          <button onClick={onClear} data-testid={`${testId}-clear`} style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <X size={11} /> Remove
          </button>
        </>
      ) : (
        <>
          <ScanFace size={36} style={{ color: "#475569" }} />
          <button onClick={() => inputRef.current?.click()} data-testid={`${testId}-pick`} style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: 6, color: "white", padding: "10px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Upload size={13} /> Choose file
          </button>
          <div style={{ color: "#64748b", fontSize: 10 }}>JPG / PNG · &lt; 5 MB</div>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => onPick(e.target.files?.[0])}
        style={{ display: "none" }}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

function VerdictBadge({ label, value, color, testId }) {
  return (
    <div style={{ background: "#080c12", border: `1px solid ${color}55`, borderRadius: 8, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 4, minWidth: 110 }}>
      <span style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <span data-testid={testId} style={{ color, fontSize: 14, fontWeight: 800 }}>{value}</span>
    </div>
  );
}

export default function FaceMatchPage() {
  const [selfie, setSelfie] = useState(null);
  const [idPhoto, setIdPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("mock"); // "mock" | "live"

  const pick = async (which, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    const preview = URL.createObjectURL(file);
    const obj = { file, name: file.name, size: file.size, preview };
    which === "selfie" ? setSelfie(obj) : setIdPhoto(obj);
  };

  const clear = (which) => {
    if (which === "selfie") { selfie?.preview && URL.revokeObjectURL(selfie.preview); setSelfie(null); }
    else { idPhoto?.preview && URL.revokeObjectURL(idPhoto.preview); setIdPhoto(null); }
  };

  const verify = async () => {
    if (!selfie || !idPhoto) { toast.error("Upload both selfie and ID photo"); return; }
    setLoading(true); setResult(null);
    try {
      const [selfie_base64, id_photo_base64] = await Promise.all([
        readAsBase64(selfie.file),
        readAsBase64(idPhoto.file),
      ]);
      const { data } = await axios.post(API, { selfie_base64, id_photo_base64 }, {
        withCredentials: true,
        timeout: 30000,
        headers: { "Content-Type": "application/json" },
      });
      setResult(data.result);
      setMode(data.rekognition_source || (data.mock_mode === false ? "live" : "mock"));
      const verdict = data.result?.overall_result;
      if (verdict === "PASS") toast.success("PASS — face matched");
      else if (verdict === "FAIL") toast.error("FAIL — face verification rejected");
      else toast.warning("MANUAL REVIEW required");
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      toast.error(isTimeout ? "Verification timed out — retry" : (e.response?.data?.detail || "Verification failed"));
    } finally { setLoading(false); }
  };

  const reset = () => { clear("selfie"); clear("idPhoto"); setResult(null); };

  const overall = result?.overall_result;
  const risk = result?.risk_level;
  const fm = result?.face_match;
  const liveness = result?.liveness;
  const deepfake = result?.deepfake;
  const fq = result?.face_quality;

  return (
    <div data-testid="face-match-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <ScanFace style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Face Match Verification</h1>
          <span
            data-testid="provider-badge-didit"
            style={{
              background: mode === "live" ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.15)",
              color: mode === "live" ? "#22c55e" : "#f59e0b",
              border: `1px solid ${mode === "live" ? "#22c55e55" : "#f59e0b55"}`,
              fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: 0.5,
            }}
          >
            {mode === "live" ? "LIVE · Didit" : "MOCK · Didit"}
          </span>
          <span style={{ background: "rgba(148,163,184,0.12)", color: "#cbd5e1", border: "1px solid #94a3b855", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Session-only</span>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 20 }}>
        Compare a live selfie against the ID-card photo. Powered by HyperVerge verifyPair (live mode)
        with a deterministic mock fallback when credentials are absent — zero UI change on swap.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <UploadBox
          label="Selfie (Live Photo)"
          file={selfie}
          onPick={(f) => pick("selfie", f)}
          onClear={() => clear("selfie")}
          testId="selfie"
        />
        <UploadBox
          label="ID Card Photo"
          file={idPhoto}
          onPick={(f) => pick("idPhoto", f)}
          onClear={() => clear("idPhoto")}
          testId="id-photo"
        />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
        <button
          onClick={verify}
          disabled={loading || !selfie || !idPhoto}
          data-testid="verify-btn"
          style={{
            background: loading || !selfie || !idPhoto ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
            border: "none", color: "white", padding: "12px 22px", borderRadius: 6,
            fontSize: 13, fontWeight: 700, cursor: loading || !selfie || !idPhoto ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 8,
          }}
        >
          {loading ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
          {loading ? "Verifying…" : "Verify Face Match"}
        </button>
        {(selfie || idPhoto || result) && (
          <button onClick={reset} data-testid="reset-btn" style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "12px 18px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={12} /> Reset
          </button>
        )}
      </div>

      {result && (
        <>
        <div style={{ marginBottom: 16 }}>
          <IdentityResultCard person={fromFaceMatch(result, selfie?.preview || null)} />
        </div>
        <div data-testid="face-result-card" style={{ background: "#0d1117", border: `1px solid ${VERDICT_COLOR[overall] || "#1e2530"}`, borderRadius: 12, padding: 22 }}>
          {/* Verdict header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            {overall === "PASS"
              ? <CheckCircle2 size={22} style={{ color: "#22c55e" }} />
              : overall === "FAIL"
                ? <XCircle size={22} style={{ color: "#dc2626" }} />
                : <AlertTriangle size={22} style={{ color: "#f59e0b" }} />}
            <div style={{ flex: 1 }}>
              <div data-testid="overall-result" style={{ color: VERDICT_COLOR[overall] || "#f1f5f9", fontSize: 20, fontWeight: 800 }}>
                {overall || "—"}
              </div>
              <div style={{ color: "#94a3b8", fontSize: 12 }}>{result?.compliance_note}</div>
            </div>
            <VerdictBadge label="Risk" value={risk || "—"} color={RISK_COLOR[risk] || "#94a3b8"} testId="risk-level" />
            <VerdictBadge label="Action" value={result?.recommended_action || "—"} color={VERDICT_COLOR[overall] || "#94a3b8"} testId="recommended-action" />
          </div>

          {/* Pillars */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
            <Pillar
              title="Face Match"
              big={`${(fm?.confidence ?? 0).toFixed(1)}%`}
              sub={fm?.verdict}
              color={fm?.verdict === "MATCH" ? "#22c55e" : fm?.verdict === "LOW_CONFIDENCE" ? "#f59e0b" : "#dc2626"}
              testId="pillar-face-match"
            />
            <Pillar
              title="Liveness"
              big={liveness?.score ?? 0}
              sub={liveness?.verdict}
              color={liveness?.verdict === "LIVE" ? "#22c55e" : liveness?.verdict === "INCONCLUSIVE" ? "#f59e0b" : "#dc2626"}
              testId="pillar-liveness"
            />
            <Pillar
              title="Deepfake Risk"
              big={deepfake?.risk || "LOW"}
              sub={`${deepfake?.signals?.length || 0} signal(s)`}
              color={RISK_COLOR[deepfake?.risk] || "#94a3b8"}
              testId="pillar-deepfake"
            />
            <Pillar
              title="Image Quality"
              big={`${fq?.score ?? 0}`}
              sub={`Sharp ${fq?.sharpness} · Bright ${fq?.brightness} · ${fq?.verdict}`}
              color={fq?.verdict === "GOOD" ? "#22c55e" : fq?.verdict === "ACCEPTABLE" ? "#f59e0b" : "#dc2626"}
              testId="pillar-quality"
            />
          </div>

          {/* Deepfake signals */}
          {deepfake?.signals?.length > 0 && (
            <div style={{ background: "rgba(220,38,38,0.06)", border: "1px solid #dc262644", borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ color: "#fca5a5", fontSize: 11, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <Eye size={11} /> DEEPFAKE / SPOOF SIGNALS DETECTED
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {deepfake.signals.map((s, i) => (
                  <span key={s} data-testid={`signal-${i}`} style={{ background: "#dc262622", color: "#fca5a5", border: "1px solid #dc262655", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontFamily: "ui-monospace, monospace" }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Risk flags */}
          {result?.risk_flags?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Risk Flags</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {result.risk_flags.map((f, i) => {
                  const sev = (f.severity || "MEDIUM").toUpperCase();
                  const c = sev === "WARNING" ? "#f59e0b" : (RISK_COLOR[sev] || "#f59e0b");
                  return (
                    <div key={`${f.flag}-${i}`} data-testid={`risk-flag-${i}`} style={{ background: `${c}10`, border: `1px solid ${c}44`, borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AlertTriangle size={12} style={{ color: c }} />
                        <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>{f.flag}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{sev}</span>
                      </div>
                      {f.action && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2, marginLeft: 20 }}>→ {f.action}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Meta footer */}
          <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 10, marginTop: 8, paddingTop: 8, borderTop: "1px solid #1e2530" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Sparkles size={9} /> {result.ai_mode || "—"} · {result.source}
            </span>
            <span>Cost ₹{result.cost_inr?.toFixed(2)} · Threshold {fm?.threshold_used}%</span>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

function Pillar({ title, big, sub, color, testId }) {
  return (
    <div data-testid={testId} style={{ background: "#080c12", border: `1px solid ${color}55`, borderRadius: 8, padding: 12 }}>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
      <div style={{ color, fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{big}</div>
      <div style={{ color: "#94a3b8", fontSize: 10, marginTop: 4 }}>{sub}</div>
    </div>
  );
}
